// What the ads screens say, as plain functions over the API's answers, so each rule has a test and no screen does its own arithmetic.
import { targetVerdict, type AdCampaignDto, type AdGuardSettingsDto, type FunnelTargetCheck, type MoneyDto } from "@imagora/contracts";
import type { AdActionDto, AdAlertDto, AdCellDecisionDto, AdCellDto, AdConnectionDto, AdEntityStatus, AdHoldStatus, AdPromotionStatus, AdGuardStatusDto, AdMetricTotalDto, CreativeDto, TopicDto, UiLang } from "@imagora/contracts";
import { localeOf } from "../../lib/format";
import type { TextKey } from "../../i18n/keys";
import { todayIn } from "../../lib/dates";

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";

// ISO 4217 exponents other than 2. A fixed table, as on the server: the browser's own idea of a currency's digits varies between runtimes.
const OTHER_DIGITS: Readonly<Record<string, number>> = { BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0, BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3, CLF: 4, UYW: 4 };

export const currencyDigits = (currency: string): number => OTHER_DIGITS[currency.toUpperCase()] ?? 2;

/** An amount as a person reads it, "12 lei" or "12,50 €", never minor units. */
export function formatAmount(minor: number, currency: string, lang: UiLang): string {
  const digits = currencyDigits(currency);
  const value = minor / 10 ** digits;
  const whole = Number.isInteger(value);
  return new Intl.NumberFormat(localeOf(lang), { style: "currency", currency, currencyDisplay: "narrowSymbol", minimumFractionDigits: whole ? 0 : digits, maximumFractionDigits: digits }).format(value);
}

export const toMinor = (major: number, currency: string): number => Math.round(major * 10 ** currencyDigits(currency));
export const toMajor = (minor: number, currency: string): number => minor / 10 ** currencyDigits(currency);

/** What a person typed, as minor units; null when it is not a usable amount. Accepts "12,5" as well as "12.5". */
export function parseAmount(text: string, currency: string): number | null {
  const value = Number(text.trim().replace(",", "."));
  if (text.trim() === "" || !Number.isFinite(value) || value <= 0) return null;
  return toMinor(value, currency);
}

export const PERIODS = ["today", "7d", "30d"] as const;
export type Period = (typeof PERIODS)[number];
const PERIOD_DAYS: Record<Period, number> = { today: 1, "7d": 7, "30d": 30 };

const shiftDay = (day: string, by: number): string => new Date(Date.parse(`${day}T00:00:00Z`) + by * 86_400_000).toISOString().slice(0, 10);

/** The days a period covers, in the tenant's time zone. "7 days" counts today. */
export function periodRange(period: Period, timeZone: string, now: Date = new Date()): { from: string; to: string } {
  const to = todayIn(timeZone, now);
  return { from: shiftDay(to, 1 - PERIOD_DAYS[period]), to };
}

export const costPerLead = (spendMinor: number, leads: number): number | null => (leads > 0 ? Math.round(spendMinor / leads) : null);

export interface RowNumbers {
  spendMinor: number;
  leads: number;
  clicks: number;
  impressions: number;
  cplMinor: number | null;
}

export const NO_NUMBERS: RowNumbers = { spendMinor: 0, leads: 0, clicks: 0, impressions: 0, cplMinor: null };

export function numbersByExternalId(rows: ReadonlyArray<AdMetricTotalDto>): Map<string, RowNumbers> {
  const out = new Map<string, RowNumbers>();
  for (const row of rows) {
    out.set(`${row.connectionId}:${row.externalId}`, { spendMinor: row.spendMinor, leads: row.leads, clicks: row.clicks, impressions: row.impressions, cplMinor: costPerLead(row.spendMinor, row.leads) });
  }
  return out;
}

export interface Named {
  name: string;
  currency: string;
  numbers: RowNumbers;
}

/**
 * The row the sentence on top talks about: the cheapest lead, among rows with a lead. Only rows in the same currency can
 * be compared, so the currency that spent the most decides which ones count. Null when nothing has brought a lead.
 */
export function bestRow<T extends Named>(rows: ReadonlyArray<T>): T | null {
  const spent = new Map<string, number>();
  for (const row of rows) spent.set(row.currency, (spent.get(row.currency) ?? 0) + row.numbers.spendMinor);
  const main = [...spent.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  let best: T | null = null;
  for (const row of rows) {
    if (row.currency !== main || row.numbers.cplMinor === null) continue;
    if (best === null || row.numbers.cplMinor < best.numbers.cplMinor!) best = row;
  }
  return best;
}

export interface CurrencyTotal {
  currency: string;
  spendMinor: number;
  leads: number;
  clicks: number;
  impressions: number;
  cplMinor: number | null;
}

/** Totals per currency: lei and euro never add up to one number. */
export function totalsByCurrency(rows: ReadonlyArray<Named>): CurrencyTotal[] {
  const out = new Map<string, CurrencyTotal>();
  for (const { currency, numbers } of rows) {
    const total = out.get(currency) ?? { currency, spendMinor: 0, leads: 0, clicks: 0, impressions: 0, cplMinor: null };
    total.spendMinor += numbers.spendMinor;
    total.leads += numbers.leads;
    total.clicks += numbers.clicks;
    total.impressions += numbers.impressions;
    out.set(currency, total);
  }
  return [...out.values()].map((total) => ({ ...total, cplMinor: costPerLead(total.spendMinor, total.leads) }));
}

export const entityTone = (status: AdEntityStatus): Tone => (status === "active" ? "success" : status === "paused" ? "warning" : "neutral");

export const creationTone = (status: AdPromotionStatus): Tone => {
  if (status === "running" || status === "decided") return "success";
  if (status === "failed" || status === "unconfirmed") return "danger";
  if (status === "paused" || status === "dry_run" || status === "creating" || status === "stopped_by_system") return "warning";
  return "neutral";
};

/** A stop that did not land first, because it may be spending; then what a person can act on; then what only waits. */
const HOLD_RANK: Record<AdHoldStatus, number> = { pause_failed: 0, back_in_stock: 1, holding: 2, restarted: 3, dismissed: 3 };
export const holdRank = (status: AdHoldStatus): number => HOLD_RANK[status];
export const isOpenHold = (status: AdHoldStatus): boolean => status === "pause_failed" || status === "back_in_stock" || status === "holding";
export const holdTone = (status: AdHoldStatus): Tone => {
  if (status === "pause_failed") return "danger";
  if (status === "back_in_stock") return "info";
  if (status === "holding") return "warning";
  return "neutral";
};

export interface ConnectionView {
  tone: Tone;
  /** What is wrong or what comes next, in plain words. Empty when nothing is missing. */
  missing: TextKey[];
  needsSignIn: boolean;
  needsAccount: boolean;
}

export function connectionView(connection: AdConnectionDto): ConnectionView {
  switch (connection.status) {
    case "pending":
      return { tone: "warning", missing: ["ads.connections.missing.signIn"], needsSignIn: true, needsAccount: false };
    case "needs_account":
      return { tone: "warning", missing: ["ads.connections.missing.account"], needsSignIn: false, needsAccount: true };
    case "expired":
      return { tone: "danger", missing: ["ads.connections.missing.expired"], needsSignIn: true, needsAccount: false };
    case "error":
      return { tone: "danger", missing: ["ads.connections.missing.error"], needsSignIn: true, needsAccount: false };
    case "disconnected":
      return { tone: "neutral", missing: ["ads.connections.missing.disconnected"], needsSignIn: true, needsAccount: false };
    default: {
      const missing: TextKey[] = [];
      if (!connection.lastSyncAt) missing.push("ads.connections.missing.neverSynced");
      if (connection.spendSyncError) missing.push("ads.connections.missing.spendRead");
      return { tone: missing.length > 0 ? "warning" : "success", missing, needsSignIn: false, needsAccount: false };
    }
  }
}

export const usableConnections = (connections: ReadonlyArray<AdConnectionDto>, platform?: "meta" | "google"): AdConnectionDto[] =>
  connections.filter((row) => row.status === "active" && row.currency && (!platform || row.platform === platform));

export type CapUse = { kind: "none" } | { kind: "known"; usedMinor: number; capMinor: number; percent: number; over: boolean };

export function capUse(usedMinor: number, capMinor: number | null): CapUse {
  if (capMinor === null) return { kind: "none" };
  return { kind: "known", usedMinor, capMinor, percent: capMinor > 0 ? Math.round((usedMinor / capMinor) * 100) : 100, over: usedMinor >= capMinor };
}

/** The banner's reasons, without repeats. */
export function unknownReasons(status: AdGuardStatusDto): string[] {
  return [...new Set(status.connections.map((row) => row.unknownReason).filter((reason): reason is NonNullable<typeof reason> => reason !== null))];
}

/** A test's total a day: each variant has this budget of its own. */
export const cellDailyTotal = (budgetPerVariantMinor: number, variants: number): number => budgetPerVariantMinor * variants;

/** A topic the ads flow takes: the same rule the API checks, so the list only offers what will pass. */
export const topicFitsAds = (topic: Pick<TopicDto, "status" | "lifecycle" | "channel" | "priority" | "factIds">): boolean =>
  topic.status === "in_product" && topic.lifecycle === "active" && (topic.channel === "ad" || topic.channel === "both") && topic.priority !== "no" && topic.factIds.length > 0;

const META_PLATFORMS = new Set(["meta", "facebook", "instagram"]);

export interface AngleGroup {
  angle: string;
  creatives: CreativeDto[];
}

/** Approved Meta creatives of a topic, by angle: a test compares one angle, so one group is what can be picked from. */
export function creativesByAngle(creatives: ReadonlyArray<CreativeDto>, topicId: string): AngleGroup[] {
  const groups = new Map<string, CreativeDto[]>();
  for (const creative of creatives) {
    if (creative.topicId !== topicId || !META_PLATFORMS.has(creative.platform)) continue;
    if (creative.status !== "approved" && creative.status !== "scheduled") continue;
    const angle = typeof creative.varied.angle === "string" ? creative.varied.angle : "";
    groups.set(angle, [...(groups.get(angle) ?? []), creative]);
  }
  return [...groups.entries()].map(([angle, list]) => ({ angle, creatives: list })).sort((a, b) => b.creatives.length - a.creatives.length);
}

export const hookOf = (creative: Pick<CreativeDto, "varied" | "body">): string => (typeof creative.varied.hook === "string" && creative.varied.hook ? creative.varied.hook : creative.body.split("\n")[0]!.slice(0, 80));

export interface DecisionArm {
  id: string;
  letter: string;
  costPerEventMinor: number | null;
  qualificationRate: number | null;
  pBest: number | null;
  starved: boolean;
}

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === "object" ? (value as Record<string, unknown>) : {});
const asNumber = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

/** The arm numbers of the newest decision that has any, with the letters a person knows the variants by. */
export function latestArms(decisions: ReadonlyArray<AdCellDecisionDto>, cell: Pick<AdCellDto, "variants">): DecisionArm[] {
  const newest = [...decisions].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).find((row) => Array.isArray(asRecord(row.stats).arms));
  if (!newest) return [];
  const letters = new Map(cell.variants.map((variant) => [variant.id, variant.letter]));
  return (asRecord(newest.stats).arms as unknown[]).flatMap((raw) => {
    const arm = asRecord(raw);
    const id = typeof arm.id === "string" ? arm.id : "";
    const letter = letters.get(id);
    if (!letter) return [];
    return [{ id, letter, costPerEventMinor: asNumber(arm.costPerEventMinor), qualificationRate: asNumber(arm.qualificationRate), pBest: asNumber(arm.pBest), starved: arm.starved === true }];
  });
}

export function newestFirst<T extends { createdAt: string }>(rows: ReadonlyArray<T>): T[] {
  return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** The journal's two sides of a change, as what the screen can state. */
export function actionChange(action: Pick<AdActionDto, "action" | "before" | "after">): { kind: "status"; from: string | null; to: string | null } | { kind: "budget"; from: number | null; to: number | null } | { kind: "none" } {
  const before = asRecord(action.before);
  const after = asRecord(action.after);
  if ("dailyBudgetMinor" in after || "dailyBudgetMinor" in before) return { kind: "budget", from: asNumber(before.dailyBudgetMinor), to: asNumber(after.dailyBudgetMinor) };
  if (typeof after.status === "string" || typeof before.status === "string") return { kind: "status", from: typeof before.status === "string" ? before.status : null, to: typeof after.status === "string" ? after.status : null };
  return { kind: "none" };
}

/** The guards and the engine stopped it, as opposed to a person pressing the button. */
export const isAutomatic = (action: Pick<AdActionDto, "source">): boolean => action.source !== "user";

export const openAlerts = (alerts: ReadonlyArray<AdAlertDto>): AdAlertDto[] => alerts.filter((row) => row.ackedAt === null && row.resolvedAt === null);

/** The countries a Search campaign can be aimed at from here, with the platform's own location ids. */
export const SEARCH_LOCATIONS: ReadonlyArray<{ id: string; country: string }> = [
  { id: "2642", country: "RO" },
  { id: "2100", country: "BG" },
  { id: "2348", country: "HU" },
  { id: "2300", country: "GR" },
  { id: "2498", country: "MD" },
];

export const countryName = (code: string, lang: UiLang): string => {
  try {
    return new Intl.DisplayNames([localeOf(lang)], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};

export const languageName = (code: string, lang: UiLang): string => {
  try {
    return new Intl.DisplayNames([localeOf(lang)], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
};

export interface CampaignsSearch {
  campaign?: string;
  adset?: string;
  period?: Period;
}

export function validateCampaignsSearch(search: Record<string, unknown>): CampaignsSearch {
  return {
    ...(typeof search.campaign === "string" ? { campaign: search.campaign } : {}),
    ...(typeof search.adset === "string" ? { adset: search.adset } : {}),
    ...(PERIODS.includes(search.period as Period) ? { period: search.period as Period } : {}),
  };
}

/** The address a person recognises: the tracking tags on the link are ours, not theirs to read. */
export function withoutTags(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname === "/" ? "" : parsed.pathname}`;
  } catch {
    return url;
  }
}

type CampaignTargets = Pick<AdCampaignDto, "targetCurrency" | "targetCostPerLeadMinor" | "targetCostPerClientMinor">;
type TenantTargets = Pick<AdGuardSettingsDto, "capCurrency" | "targetCostPerLeadMinor" | "targetCostPerClientMinor">;

const moneyOf = (currency: string | null | undefined, minor: number | null | undefined): MoneyDto | null => (currency && minor !== null && minor !== undefined ? { currency, minor } : null);

/** A campaign's own target when it set one, otherwise the tenant's. */
export const leadTargetFor = (campaign: CampaignTargets | undefined, tenant: TenantTargets | undefined): MoneyDto | null =>
  moneyOf(campaign?.targetCurrency, campaign?.targetCostPerLeadMinor) ?? moneyOf(tenant?.capCurrency, tenant?.targetCostPerLeadMinor);

export function costCheck(input: { costMinor: number | null; spendMinor: number; currency: string; target: MoneyDto | null }): FunnelTargetCheck {
  const spend = input.spendMinor > 0 ? [{ currency: input.currency, minor: input.spendMinor }] : [];
  const cost = moneyOf(input.currency, input.costMinor);
  return { target: input.target, verdict: targetVerdict({ cost, spend, target: input.target }) };
}

/** Text for an input from a stored amount, and back: blank means none. */
export const amountText = (minor: number | null | undefined, currency: string): string => (minor === null || minor === undefined ? "" : String(toMajor(minor, currency)));
